import React, {useState, useEffect, useRef} from "react";
import { disableAnalytics, identifyAnonymous, identifyUser, initAnalytics, pageView, setGAUserIdAllowed, setMetaConsent } from '../libs/analyticsManager';
import { useIsEU } from '../hooks/useIsEU';
import { EUOnlyCookieConsent } from './EUOnlyCookieConsent';
import { useLoginState } from '../studioxr/auth/LoginState';
import { useCreditStore } from "ui";


export const EUCookieAndAnalytics = ({ currentUser }) => {
    const profile = useLoginState((state) => state.profile);
    const isEU = useIsEU();
    const [analyticsEnabled, setAnalyticsEnabled] = useState(false);
    const balance = useCreditStore((state) => state.balance);
    const teamBalance = useCreditStore((state) => state.teamBalance);
    const lastCreditKeyRef = useRef('');
    const identifiedPageViewedForUidRef = useRef<string | null>(null);
    const [consentStatus, setConsentStatus] = useState<'accepted' | 'declined' | null>(() => {
        try {
            const stored = localStorage.getItem('cookieConsent');
            if (stored === 'accepted' || stored === 'declined') return stored;
        } catch {
            // localStorage unavailable (restricted browser, iframe, etc.)
        }
        return null;
    });
    // True once the user clicked Accept/Decline in THIS session — a stale
    // profile value must never override an explicit in-session decision.
    const decidedThisSessionRef = useRef(false);
    // A decline synced from another device, before this device's state catches up.
    const syncedDecline = isEU !== false && !decidedThisSessionRef.current &&
        (profile as { cookieConsent?: string } | null)?.cookieConsent === 'declined';

    // Cross-device consent sync (profile.cookieConsent, written on every
    // decision below — e.g. the SketchUp panel, whose CEF storage is wiped
    // between sessions), with DECLINE-WINS semantics: nothing here compares
    // recency, so the restrictive choice must hold. A synced decline applies
    // on this device (and is enforced, not just relabeled); a synced accept
    // never overrides a local decline. Users with only a localStorage
    // decision behave exactly as before. Non-EU users are untouched — their
    // analytics never gated on consent.
    useEffect(() => {
        if (decidedThisSessionRef.current) return;
        if (isEU === false) return;
        const fromProfile = (profile as { cookieConsent?: string } | null)?.cookieConsent;
        if (fromProfile !== 'accepted' && fromProfile !== 'declined') return;
        setConsentStatus((current) => {
            if (fromProfile === 'declined') return 'declined';
            return current === 'declined' ? current : 'accepted';
        });
        if (fromProfile === 'declined') {
            // Enforce the decline: analytics may already be running from this
            // device's stored accept, and next session's init race reads
            // localStorage before the profile loads — mirror it locally.
            try { localStorage.setItem('cookieConsent', 'declined'); } catch {}
            disableAnalytics();
            // Now, not on the next render: this render's identify effect still
            // sees analytics as enabled.
            setGAUserIdAllowed(false);
            setAnalyticsEnabled(false);
        }
    }, [profile, isEU]);

      // Non-EU: init immediately
      useEffect(() => {
        if (isEU === false && !analyticsEnabled) {
          initAnalytics();
          setAnalyticsEnabled(true);
        }
      }, [isEU, analyticsEnabled]);

      // EU and previous consent given. Signed in: wait for this account's
      // profile, since its synced decline wins and Userpilot can't be stopped
      // once it has started loading (#4414).
      const profileReady = !currentUser || profile?.uid === currentUser.uid;
      useEffect(() => {
        if (isEU && consentStatus === 'accepted' && !analyticsEnabled && profileReady && !syncedDecline) {
          initAnalytics();
          setAnalyticsEnabled(true);
        }
      }, [isEU, consentStatus, analyticsEnabled, profileReady, syncedDecline]);

      // Grant for non-EU or EU-accepted, revoke on any decline. The same rule
      // decides whether GA4 events may carry the account id.
      useEffect(() => {
        if (isEU === undefined || isEU === null) return;
        const granted = isEU === false || (consentStatus === 'accepted' && !syncedDecline);
        setMetaConsent(granted);
        setGAUserIdAllowed(granted);
      }, [isEU, consentStatus, syncedDecline]);

      // Anonymous path: identifyAnonymous + pageView when there's no user.
      // Userpilot queues events until identify/anonymous fires, so we anonymously
      // identify logged-out visitors first.
      useEffect(() => {
        if (!analyticsEnabled || currentUser) return;
        identifyAnonymous();
        pageView();
      }, [analyticsEnabled, currentUser]);

      // Identified path: identifyUser is idempotent (also used for profile/credit
      // updates). Userpilot doesn't stitch anonymous events forward, so fire one
      // pageView per uid to attribute the current URL to the real user.
      useEffect(() => {
        if (!analyticsEnabled || !currentUser || !profile || syncedDecline) return;
        identifyUser({currentUser, profile});
        if (identifiedPageViewedForUidRef.current !== currentUser.uid) {
          identifiedPageViewedForUidRef.current = currentUser.uid;
          pageView();
        }
      }, [analyticsEnabled, currentUser, profile, syncedDecline]);

      useEffect(() => {
        if (!analyticsEnabled || !currentUser || !profile || syncedDecline) return;
        const scope = teamBalance ? 'team' : 'personal';
        const total = teamBalance?.totalBalance ?? balance?.totalBalance;
        if (typeof total !== 'number') return;
        const key = `${scope}:${total}`;
        if (key === lastCreditKeyRef.current) return;
        lastCreditKeyRef.current = key;
        identifyUser({
          currentUser,
          profile,
          metadata: {
            creditBalance: total,
            creditScope: scope,
          },
        });
      }, [analyticsEnabled, currentUser, profile, syncedDecline, balance?.totalBalance, teamBalance?.totalBalance]);

      // A decision writes BOTH stores: localStorage (as before) and, when a
      // profile is loaded, profile.cookieConsent(+At) so the decision follows
      // the account across devices. Fire-and-forget — a profile write failure
      // must never block the consent UX.
      const persistConsentToProfile = (decision: 'accepted' | 'declined') => {
        if (!profile) return;
        try {
          const updateAndSaveProfile = (useLoginState.getState() as {
            updateAndSaveProfile?: (p: unknown) => Promise<unknown>;
          }).updateAndSaveProfile;
          updateAndSaveProfile?.({
            ...profile,
            cookieConsent: decision,
            cookieConsentAt: new Date(),
          })?.catch?.(() => undefined);
        } catch {
          // never block the banner on a profile write
        }
      };

      const handleAccept = () => {
        decidedThisSessionRef.current = true;
        try { localStorage.setItem('cookieConsent', 'accepted'); } catch {}
        persistConsentToProfile('accepted');
        initAnalytics();
        identifyUser({currentUser, profile});
        setAnalyticsEnabled(true);
        setConsentStatus('accepted');
      };

      const handleDecline = () => {
        decidedThisSessionRef.current = true;
        try { localStorage.setItem('cookieConsent', 'declined'); } catch {}
        persistConsentToProfile('declined');
        disableAnalytics();
        setGAUserIdAllowed(false);
        setAnalyticsEnabled(false);
        setConsentStatus('declined');
      };

      return (
        <>
            {isEU && consentStatus === null && (
                <EUOnlyCookieConsent
                    onAccept={handleAccept}
                    onDecline={handleDecline}
                />
            )}
        </>
      );
}
